// Copied from coulomb-commons-ui soh-band-migration 0.1.0 (src/components/chartTooltip.js).
//
// Tooltip view-models. Pure functions (no DOM, no React); <SankeyTooltip /> (SankeyTooltip.jsx) renders
// the model:
//   {
//     kind:     'node' | 'flow',
//     accents:  string[]               // 1 colour (node) or 2 (flow: from -> to)
//     title:    string,
//     subtitle: string,
//     metric:   { value: string, unit: string, hint?: string },
//     rows:     { label, value, tone?: 'up'|'down'|'flat', swatch?: string }[],
//     footer?:  string,
//   }
// Bucket labels are used as given (e.g. "SOH 95-100"), so the chart adds no prefix.

const ARROW = "→";
export const formatShare = (n, of) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : "—");
const signed = (n) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : "0");
const tone = (n) => (n > 0 ? "up" : n < 0 ? "down" : "flat");

function nounFor(n, singular, plural) {
  return n === 1 ? singular : plural;
}

/**
 * @param {object} nodeMeta  { id, columnIndex, bucketIndex, count }
 * @param {object} ctx       { graph, columns, buckets, nounSingular, nounPlural, clickable }
 */
export function nodeTooltipModel(nodeMeta, ctx) {
  const { graph, columns, buckets, nounSingular, nounPlural, clickable = true } = ctx;
  const bucket = buckets[nodeMeta.bucketIndex];
  const columnTotal = graph.distributions[nodeMeta.columnIndex].reduce((a, b) => a + b, 0);

  let stayed = 0;
  let movedIn = 0;
  let movedOut = 0;
  graph.edgeMeta.forEach((e) => {
    if (e.target === nodeMeta.id) {
      if (e.fromBucket === e.toBucket) stayed += e.value;
      else movedIn += e.value;
    }
    if (e.source === nodeMeta.id && e.fromBucket !== e.toBucket) movedOut += e.value;
  });

  const rows = [];
  const prev = nodeMeta.columnIndex > 0 ? columns[nodeMeta.columnIndex - 1] : null;
  const next = nodeMeta.columnIndex < columns.length - 1 ? columns[nodeMeta.columnIndex + 1] : null;

  if (prev) {
    const prevCount = graph.distributions[nodeMeta.columnIndex - 1][nodeMeta.bucketIndex];
    const net = nodeMeta.count - prevCount;
    rows.push({ label: `vs ${prev.label}`, value: `${signed(net)} (${prevCount} ${ARROW} ${nodeMeta.count})`, tone: tone(net) });
    rows.push({ label: `Stayed from ${prev.label}`, value: String(stayed) });
    rows.push({ label: "Moved in", value: String(movedIn), tone: movedIn ? "up" : "flat" });
  }
  if (next) {
    rows.push({ label: `Moving out by ${next.label}`, value: String(movedOut), tone: movedOut ? "down" : "flat" });
  }

  return {
    kind: "node",
    accents: [bucket.color],
    title: bucket.label,
    subtitle: columns[nodeMeta.columnIndex].label,
    metric: {
      value: String(nodeMeta.count),
      unit: nounFor(nodeMeta.count, nounSingular, nounPlural),
      hint: `${formatShare(nodeMeta.count, columnTotal)} of ${nounPlural}`,
    },
    rows,
    footer: clickable ? `Click to view ${nounPlural}` : undefined,
  };
}

/**
 * @param {object} flowMeta  { fromIndex, fromBucket, toBucket, value }
 * @param {object} ctx       { graph, columns, buckets, nounSingular, nounPlural, clickable }
 */
export function flowTooltipModel(flowMeta, ctx) {
  const { graph, columns, buckets, nounSingular, nounPlural, clickable = false } = ctx;
  const from = buckets[flowMeta.fromBucket];
  const to = buckets[flowMeta.toBucket];
  const stayed = flowMeta.fromBucket === flowMeta.toBucket;
  const fromCount = graph.distributions[flowMeta.fromIndex][flowMeta.fromBucket];
  const toCount = graph.distributions[flowMeta.fromIndex + 1][flowMeta.toBucket];
  const a = columns[flowMeta.fromIndex].label;
  const b = columns[flowMeta.fromIndex + 1].label;

  return {
    kind: "flow",
    accents: stayed ? [from.color] : [from.color, to.color],
    title: stayed ? `Stayed at ${from.label}` : `Moved from ${from.label} to ${to.label}`,
    subtitle: `${a} ${ARROW} ${b}`,
    metric: {
      value: String(flowMeta.value),
      unit: nounFor(flowMeta.value, nounSingular, nounPlural),
      hint: stayed ? "unchanged" : "moved",
    },
    rows: [
      { label: `Of ${from.label} in ${a}`, value: formatShare(flowMeta.value, fromCount), swatch: from.color },
      { label: `Of ${to.label} in ${b}`, value: formatShare(flowMeta.value, toCount), swatch: to.color },
    ],
    footer: clickable ? `Click to view ${nounPlural}` : undefined,
  };
}
